import React, { useState, useEffect, useMemo, useRef } from 'react';
import Modal from './Modal';
import Button from './Button';
import { Employee, LeaveCategory, LeaveEntry, NameDisplayFormat, DisplayGroup, Shift } from '../../shared/types.js';
import { calculateEndDate, formatEmployeeName } from '../utils.js';

interface AddLeaveModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: Omit<LeaveEntry, 'id' | 'createdAt'>) => void;
    employees: Employee[];
    leaveCategories: LeaveCategory[];
    getGroupForEmployee: (employeeId: string) => DisplayGroup | undefined;
    shifts: Shift[];
    lockTimestamp?: number;
}

const toDateInputString = (d: Date) => {
    const date = new Date(d);
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().split('T')[0];
};

const AddLeaveModal: React.FC<AddLeaveModalProps> = ({ isOpen, onClose, onSave, employees, leaveCategories, getGroupForEmployee, shifts, lockTimestamp }) => {
    const formRef = useRef<HTMLFormElement>(null);
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
    const [leaveCategoryId, setLeaveCategoryId] = useState<string>('');
    const [startDate, setStartDate] = useState(toDateInputString(new Date()));
    const [hoursPerDay, setHoursPerDay] = useState(8);
    const [days, setDays] = useState(1);
    const [skipSaturday, setSkipSaturday] = useState(true);
    const [skipSunday, setSkipSunday] = useState(true);
    const [shiftId, setShiftId] = useState<string | null>(null);
    const sortedEmployees = useMemo(() => [...employees].sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || '')), [employees]);
    const customLeaveCategories = useMemo(() => leaveCategories.filter(c => c.source === 'kiosk'), [leaveCategories]);
    const endDate = useMemo(() => {
        try {
            const start = new Date(startDate);
            const employeeTimezoneOffset = start.getTimezoneOffset() * 60000;
            const correctedStart = new Date(start.getTime() + employeeTimezoneOffset);
            return calculateEndDate(correctedStart, Math.max(1, days), skipSaturday, skipSunday);
        } catch(e) {
            return new Date();
        }
    }, [startDate, days, skipSaturday, skipSunday]);
    useEffect(() => {
        if (!isOpen) {
            setSelectedEmployeeIds([]);
            setLeaveCategoryId('');
            setStartDate(toDateInputString(new Date()));
            setHoursPerDay(8);
            setDays(1);
            setSkipSaturday(true);
            setSkipSunday(true);
        } else {
            if (!leaveCategoryId && customLeaveCategories.length > 0) {
                setLeaveCategoryId(customLeaveCategories[0].id);
            }
        }
    }, [isOpen]);
    useEffect(() => {
        if (selectedEmployeeIds.length > 0) {
            const firstEmployeeId = selectedEmployeeIds[0];
            const group = getGroupForEmployee(firstEmployeeId);
            setShiftId(group?.settings.shiftId || null);
        } else {
            setShiftId(null);
        }
    }, [selectedEmployeeIds, getGroupForEmployee]);
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                formRef.current?.requestSubmit();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onSave]);
    const handleSelectEmployee = (id: string) => {
        setSelectedEmployeeIds(prev =>
            prev.includes(id) ? prev.filter(empId => empId !== id) : [...prev, id]
        );
    };
    const handleSelectAll = () => setSelectedEmployeeIds(employees.map(e => e.id));
    const handleSelectNone = () => setSelectedEmployeeIds([]);
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if(selectedEmployeeIds.length === 0) {
            alert('Please select at least one employee.');
            return;
        }
         if(!leaveCategoryId) {
            alert('Please select a leave category.');
            return;
        }
        const start = new Date(startDate);
        const employeeTimezoneOffset = start.getTimezoneOffset() * 60000;
        const startTimestamp = new Date(start.getTime() + employeeTimezoneOffset).getTime();
        if (lockTimestamp && startTimestamp < lockTimestamp) {
            alert("Cannot add leave into a closed payroll period.");
            return;
        }
        onSave({
            employeeIds: selectedEmployeeIds,
            leaveCategoryId,
            startDate: startTimestamp,
            endDate: endDate.getTime(),
            days,
            hoursPerDay,
            skipSaturday,
            skipSunday,
        });
    };
    const inputClasses = "block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary";
    const readonlyInputClasses = `${inputClasses} bg-gray-100 dark:bg-gray-900/50 cursor-default`;
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Add Leave Entry">
            <form ref={formRef} onSubmit={handleSubmit} className="text-gray-900 dark:text-white">
                <div className="flex flex-col md:flex-row gap-6">
                    <div className="md:w-1/3 border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-gray-50 dark:bg-gray-900/50">
                        <h3 className="text-lg font-semibold text-center mb-2 border-b border-gray-200 dark:border-gray-700 pb-1">Employees</h3>
                        <div className="h-64 overflow-y-auto pr-2">
                            {sortedEmployees.map(emp => (
                                <label key={emp.id} className="flex items-center gap-3 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedEmployeeIds.includes(emp.id)}
                                        onChange={() => handleSelectEmployee(emp.id)}
                                        className="bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded text-brand-secondary focus:ring-brand-secondary"
                                    />
                                    <span>{formatEmployeeName(emp, NameDisplayFormat.LAST_FIRST)}</span>
                                </label>
                            ))}
                        </div>
                        <div className="flex justify-around mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                             <Button type="button" size="sm" variant="ghost" onClick={handleSelectAll}>All</Button>
                             <Button type="button" size="sm" variant="ghost" onClick={handleSelectNone}>None</Button>
                        </div>
                    </div>
                    <div className="md:w-2/3 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                             <div>
                                <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Leave Type</label>
                                <select value={leaveCategoryId} onChange={e => setLeaveCategoryId(e.target.value)} required className={`mt-1 ${inputClasses}`}>
                                    {customLeaveCategories.length === 0 && <option disabled value="">No custom leave types defined</option>}
                                    {customLeaveCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Start Date</label>
                                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required className={`mt-1 ${inputClasses}`}/>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Number of Days</label>
                                <input type="number" value={days} onChange={e => setDays(parseInt(e.target.value, 10) || 1)} min="1" required className={`mt-1 ${inputClasses}`}/>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Hours Per Day</label>
                                <div className="flex items-center gap-2">
                                     <input type="number" value={hoursPerDay} onChange={e => setHoursPerDay(parseFloat(e.target.value) || 0)} min="0" step="0.5" required className={`mt-1 ${inputClasses}`}/>
                                </div>
                            </div>
                        </div>
                         <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">End Date</label>
                                <input type="text" value={endDate.toLocaleDateString()} disabled className={`mt-1 ${inputClasses} disabled:opacity-70 disabled:cursor-not-allowed`}/>
                            </div>
                             <div>
                                <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Skip Weekend Days</label>
								<div className="flex gap-4 mt-1">
									<label className="flex items-center gap-2">
										<input type="checkbox" checked={skipSaturday} onChange={e => setSkipSaturday(e.target.checked)} className="bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded text-brand-secondary focus:ring-brand-secondary"/>
										Saturday
									</label>
									 <label className="flex items-center gap-2">
										<input type="checkbox" checked={skipSunday} onChange={e => setSkipSunday(e.target.checked)} className="bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded text-brand-secondary focus:ring-brand-secondary"/>
										Sunday
									</label>
								</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-4 pt-6 mt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button type="submit">Create</Button>
                </div>
            </form>
        </Modal>
    );
};

export default AddLeaveModal;
